import { useState } from "react";
import { FormField, Input } from "@cp/ui";
import { changePassword } from "../api/authApi";
import { accountErrorMessage } from "../api/accountErrors";
import { Facts, Notice, Panel, Row, Section } from "../components/Portal";
import { useAccountAuth } from "../contexts/AccountAuthContext";
import { formatDay } from "../lib/accountFormat";
import { deleteAccountHref } from "../lib/portalCopy";

export function SecurityPage() {
  const { email, sub, createdAt, accessToken, logout, setAccessToken } = useAccountAuth();
  const [editing, setEditing] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const cancel = () => {
    if (busy) return;
    setEditing(false);
    setError(null);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const submit = async () => {
    setError(null);
    setSuccess(null);
    if (newPassword !== confirmPassword) {
      setError("Die neuen Passwörter stimmen nicht überein.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Das neue Passwort muss mindestens 8 Zeichen haben.");
      return;
    }
    setBusy(true);
    try {
      const result = await changePassword(oldPassword, newPassword, accessToken);
      setAccessToken(result.access_token);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setEditing(false);
      setSuccess("Passwort wurde geändert. Andere Sitzungen wurden beendet.");
    } catch (e) {
      setError(accountErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page" data-testid="account-security">
      <Panel className="identity">
        <Facts
          items={[
            { label: "E-Mail", value: email || "—" },
            sub ? { label: "Konto-ID", value: <span className="data data--id">{sub}</span> } : null,
            createdAt ? { label: "Angelegt am", value: <span className="data">{formatDay(createdAt)}</span> } : null,
          ]}
        />
      </Panel>

      {success ? <Notice tone="success">{success}</Notice> : null}

      <Section title="Anmeldung">
        <Panel>
          <Row
            title="Passwort"
            detail="Gilt für diese Website. Ihr lokales Tresor-Kennwort bleibt davon unberührt."
            action={
              editing ? null : (
                <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
                  Ändern
                </button>
              )
            }
          >
            {editing ? (
              <form
                className="auth-form inline-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void submit();
                }}
              >
                <FormField label="Aktuelles Passwort" htmlFor="security-old" required>
                  <Input id="security-old" type="password" autoComplete="current-password" autoFocus value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                </FormField>
                <FormField label="Neues Passwort" htmlFor="security-new" required>
                  <Input id="security-new" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                </FormField>
                <FormField label="Neues Passwort wiederholen" htmlFor="security-confirm" required>
                  <Input id="security-confirm" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                </FormField>
                {error ? <Notice tone="error">{error}</Notice> : null}
                <div className="inline-form__actions">
                  <button type="submit" className="btn-primary" disabled={busy || !oldPassword || !newPassword || !confirmPassword}>
                    {busy ? "Wird gespeichert …" : "Passwort speichern"}
                  </button>
                  <button type="button" className="btn-quiet" onClick={cancel} disabled={busy}>Abbrechen</button>
                </div>
                <p className="inline-form__note">Andere Sitzungen werden danach beendet.</p>
              </form>
            ) : null}
          </Row>
          <Row
            title="Diese Sitzung"
            detail="Beendet die Anmeldung in diesem Browser."
            action={
              <button type="button" className="btn-secondary" onClick={() => void logout()}>
                Abmelden
              </button>
            }
          />
        </Panel>
      </Section>

      <Section title="Konto löschen">
        <Panel tone="danger">
          <Row
            title="Löschung beantragen"
            detail="Anfrage per E-Mail von der Konto-Adresse. Wir löschen das Konto manuell."
            action={
              <a className="btn-danger" href={deleteAccountHref(email, sub)}>
                Anfrage schreiben
              </a>
            }
          />
        </Panel>
      </Section>
    </div>
  );
}
